import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from '../../api/nocode';
import { useAuth } from '../../context/AuthContext';
import AudioPlayer from '../../components/ui/AudioPlayer';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus } from '../../utils/campLogic';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' }
];

const SESSION_SOURCES = {
  1: { block: 'assembly', label: 'Morning Assembly' },
  2: { block: 'messhall', label: 'Midday Mess Hall' }
};

function isActive(item) {
  return item?.is_active === 1 ||
    item?.is_active === true ||
    item?.is_active === '1';
}

function EpisodeCard({ episode, session }) {
  return (
    <Card className="overflow-hidden border border-tan p-0 shadow-md">
      <div className="border-b border-tan bg-[#EAF1E6] px-6 py-5">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-amber">
          Session {session}
        </div>
        <h2 className="text-xl font-bold text-dark-green">
          {episode?.title ?? `Private Podcast Session ${session}`}
        </h2>
      </div>

      <div className="space-y-5 p-6">
        {episode?.body && (
          <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
            {episode.body}
          </p>
        )}

        <AudioPlayer
          src={episode?.video_url ?? ''}
          title={episode?.title ?? `Session ${session}`}
        />
      </div>
    </Card>
  );
}

function LockedEpisode({ session, day }) {
  const source = SESSION_SOURCES[session];

  return (
    <Card className="border-2 border-dashed border-tan py-10 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-tan">
        <SafeIcon name="Lock" className="text-2xl text-dark-green" />
      </div>
      <h3 className="text-lg font-bold text-dark-green">
        Session {session} Not Yet Open
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted">
        This episode unlocks when Day {day} {source?.label} becomes available.
      </p>
    </Card>
  );
}

function BonusEpisodeCard({ episode }) {
  return (
    <Card className="overflow-hidden border border-tan p-0 shadow-md">
      <div className="border-b border-tan bg-[#EAF1E6] px-6 py-5">
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-amber">
          Bonus Session
        </div>
        <h2 className="text-xl font-bold text-dark-green">
          {episode?.title ?? 'Private Podcast Bonus Session'}
        </h2>
      </div>

      <div className="space-y-5 p-6">
        {episode?.body && (
          <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
            {episode.body}
          </p>
        )}

        <AudioPlayer
          src={episode?.video_url ?? ''}
          title={episode?.title ?? 'Bonus Session'}
        />
      </div>
    </Card>
  );
}

function LockedBonusEpisode({ day }) {
  return (
    <Card className="border-2 border-dashed border-tan py-10 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-tan">
        <SafeIcon name="Lock" className="text-2xl text-dark-green" />
      </div>
      <h3 className="text-lg font-bold text-dark-green">
        Bonus Session Not Yet Open
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted">
        This bonus episode unlocks when Day {day} Midday Mess Hall becomes available.
      </p>
    </Card>
  );
}

export default function Podcast() {
  const { user } = useAuth();
  const location = useLocation();
  const [content, setContent] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [devMode, setDevMode] = useState(false);

  const { isAAP, isEvergreen, currentTrailDay } = getCampStatus(user, settings);
  const hasAccess = isAAP || user?.role === 'admin';

  useEffect(() => {
    const loadPodcast = async () => {
      try {
        const [contentResponse, settingsResponse] = await Promise.all([
          api.get('camp_content_gwacamp'),
          api.get('settings_gwacamp')
        ]);

        const contentRows = Array.isArray(contentResponse)
          ? contentResponse
          : contentResponse?.data ?? [];
        const settingRows = Array.isArray(settingsResponse)
          ? settingsResponse
          : settingsResponse?.data ?? [];

        setContent(contentRows ?? []);

        const settingMap = {};
        (settingRows ?? []).forEach((row) => {
          if (row?.setting_key) {
            settingMap[row.setting_key] = row?.value ?? '';
          }
        });
        setSettings(settingMap);

        const params = new URLSearchParams(location.search);
        const requestedDay = Number(params.get('day'));
        setActiveDay(
          requestedDay >= 1 && requestedDay <= 5 ? requestedDay : 1
        );
      } catch (error) {
        console.error('Failed to load private podcast', error);
      } finally {
        setLoading(false);
      }
    };

    loadPodcast();
  }, [location.search]);

  if (!hasAccess) return <Navigate to="/upgrade" replace />;

  if (loading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-muted">
        <SafeIcon name="Loader" className="mb-4 animate-spin text-4xl text-amber" />
        <p>Loading your private podcast...</p>
      </div>
    );
  }

  const getSourceContent = (day, session) => {
    const block = SESSION_SOURCES[session]?.block;

    return (content ?? []).find(
      (item) => Number(item?.day) === day && item?.block === block
    );
  };

  const getEpisode = (day, session) => {
    return (content ?? []).find(
      (item) =>
        Number(item?.day) === day &&
        item?.block === `podcast_${session}` &&
        isActive(item)
    );
  };

  const isSessionUnlocked = (day, session) => {
    if (devMode || user?.role === 'admin' || user?.early_access === 1) return true;

    const source = getSourceContent(day, session);
    if (!isActive(source)) return false;

    if (isEvergreen) return currentTrailDay >= day;

    return currentTrailDay >= day;
  };

  const daySessions = [1, 2].map((session) => ({
    session,
    episode: getEpisode(activeDay, session),
    unlocked: isSessionUnlocked(activeDay, session)
  }));

  const bonusEpisode = getEpisode(activeDay, 3);
  const bonusSession = {
    episode: bonusEpisode,
    unlocked: isSessionUnlocked(activeDay, 2)
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 pb-20 lg:flex-row">
      <aside className="w-full shrink-0 space-y-4 lg:w-72">
        <div className="rounded-r-md border-l-4 border-forest-green bg-[#EAF1E6] p-5">
          <h1 className="flex items-center gap-2 text-xl font-bold text-dark-green">
            <SafeIcon name="Headphones" className="text-amber" />
            Private Podcast
          </h1>
          <p className="mt-1 text-xs font-medium text-forest-green">
            All-Access audio sessions
          </p>
        </div>

        {user?.role === 'admin' && (
          <label className="flex cursor-pointer items-center gap-3 rounded-md border border-amber bg-amber/10 p-3 text-sm font-bold text-dark-green">
            <input
              type="checkbox"
              checked={devMode}
              onChange={(event) => setDevMode(event.target.checked)}
              className="h-4 w-4 accent-amber"
            />
            Admin: Unlock All Days
          </label>
        )}

        <nav className="space-y-2">
          {DAYS.map((item) => {
            const unlocked = [1, 2].some((session) =>
              isSessionUnlocked(item.day, session)
            );
            const active = activeDay === item.day;

            return (
              <button
                type="button"
                key={item.day}
                onClick={() => setActiveDay(item.day)}
                className={`flex w-full items-center justify-between rounded-md border p-4 text-left transition ${
                  active
                    ? 'border-forest-green bg-forest-green text-white shadow-md'
                    : 'border-tan bg-white text-dark-green hover:border-amber'
                }`}
              >
                <div>
                  <div className="mb-1 text-xs font-bold uppercase tracking-wider opacity-80">
                    Day {item.day}
                  </div>
                  <div className="font-bold">{item.title}</div>
                  <div className={`text-xs ${active ? 'text-tan' : 'text-muted'}`}>
                    {item.subtitle}
                  </div>
                </div>
                <SafeIcon
                  name={unlocked ? 'Headphones' : 'Lock'}
                  className={unlocked ? 'text-amber' : 'text-gray-400'}
                />
              </button>
            );
          })}
        </nav>
      </aside>

      <main className="min-w-0 flex-1">
        <header className="mb-7 border-b border-tan pb-5">
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-amber">
            All-Access Exclusive
          </p>
          <h2 className="text-3xl font-bold text-dark-green">
            Day {activeDay}: {DAYS[activeDay - 1].title}
          </h2>
          <p className="mt-1 text-muted">{DAYS[activeDay - 1].subtitle}</p>
        </header>

        <div className="space-y-6">
          {daySessions.map(({ session, episode, unlocked }) => (
            <React.Fragment key={`${activeDay}-${session}`}>
              {session === 2 && bonusSession.episode && !bonusSession.unlocked && (
                <LockedBonusEpisode day={activeDay} />
              )}

              {session === 2 && bonusSession.episode && bonusSession.unlocked && (
                <BonusEpisodeCard episode={bonusSession.episode} />
              )}

              {!unlocked ? (
                <LockedEpisode
                  key={`locked-${activeDay}-${session}`}
                  session={session}
                  day={activeDay}
                />
              ) : episode ? (
                <EpisodeCard
                  key={episode?.id ?? `${activeDay}-${session}`}
                  episode={episode}
                  session={session}
                />
              ) : (
                <Card
                  key={`coming-soon-${activeDay}-${session}`}
                  className="border-2 border-dashed border-tan py-10 text-center"
                >
                  <SafeIcon name="MicOff" className="mx-auto mb-4 text-4xl text-amber" />
                  <h3 className="text-lg font-bold text-dark-green">
                    Session {session} Coming Soon
                  </h3>
                  <p className="mx-auto mt-2 max-w-md text-sm text-muted">
                    Your camp director is preparing this private audio session.
                  </p>
                </Card>
              )}
            </React.Fragment>
          ))}
        </div>
      </main>
    </div>
  );
}